import React, { useState, useRef, useEffect } from 'react';
import { parse, isValid } from 'date-fns';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../shared/_TBComponents/Button';
import Icon from '../../../../shared/_TBComponents/Icon';
import Modal from '../../../../shared/_TBComponents/Modal';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import Text from '../../../../shared/_TBComponents/Text';
import Tooltip from '../../../../shared/_TBComponents/Tooltip';

const CardsContainer = styled.div`
  padding: 6px;
  position: relative;
`;

const CardWrapper = styled.div`
  position: relative;
  z-index: ${({ isMenuOpen }) => (isMenuOpen ? 20 : 1)};
  border-radius: 8px;
  margin-bottom: 12px;

  &:last-child { margin-bottom: 0; }
`;

const Card = styled.div`
  width: 100%;
  background: ${({ isExpired }) => (isExpired ? '#F5F5F5' : '#FFFAED')};
  border: 2px solid ${({ isExpired }) => (isExpired ? '#BDBDBD' : '#FFCB47')};
  border-radius: 8px;
  padding: 12px 16px;
  font-family: 'Nunito Sans';
  box-sizing: border-box;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 16px 24px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 20px 32px 0 rgba(48, 49, 51, 0.15), 0 0 1px 0 rgba(48, 49, 51, 0.08);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const CheckboxArea = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const CheckboxButton = styled.button`
  border: 1.5px solid ${({ isChecked, isExpired }) => {
    if (isChecked) return '#77A7FF';
    return isExpired ? '#BDBDBD' : '#FFCB47';
  }};
  border-radius: 3px;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  transition: border-color 0.15s;
`;

const CardDescription = styled.div`
  font-size: 19px;
  font-weight: 700;
  color: #444444;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  min-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CodeSection = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  border-top: 1px solid #BDBDBD;
  border-bottom: 1px solid #BDBDBD;
  padding: 8px 0;
`;

const CodeFields = styled.div`
  display: flex;
  align-items: stretch;
  flex: 1;
  min-width: 0;
  gap: 8px;
`;

const CodeField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
`;

const CardFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FooterValue = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 700;
  color: #9E9E9E;
`;

const MenuWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  rotate: 90deg;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
`;

const MenuDropdown = styled.div`
  position: absolute;
  top: 32px;
  right: 0;
  background: white;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 6px;
  z-index: 200;
  min-width: 130px;
`;

const DropdownRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 400;
  color: #444;
  transition: background 0.15s;

  &:hover {
    background: ${({ isDanger }) => (isDanger ? '#FFF5F5' : '#F5F5F5')};
    ${({ isDanger }) => isDanger && 'color: #FF0505;'}
    ${({ isDanger }) => isDanger && 'svg path { fill: #FF0505; }'}
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 100%;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

export const EasyJoinCard = ({
  code, onEdit, onRequestDelete, isSelected, onToggleStar, onMenuOpenChange, isNew,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);

  const closeMenu = () => {
    setIsMenuOpen(false);
    onMenuOpenChange(false);
  };

  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isMenuOpen]);

  const groups = code.groups || [];
  const allGroupNames = groups.map((group) => group.name).join(', ') || '-';
  const calendarName = code.calendar?.name || '-';
  const expiryDisplay = code.expirationDate || '—';
  const isExpired = (() => {
    if (!code.expirationDate) return false;
    const parsed = parse(code.expirationDate, 'MMMM d, yyyy', new Date());
    return isValid(parsed) && parsed < new Date();
  })();
  const accentColor = isExpired ? '#9E9E9E' : '#D2A739';
  const formattedCode = (() => {
    if (!code.joinCode) return '—';
    const s = code.joinCode.replace(/-/g, '');
    const m = Math.ceil(s.length / 2);
    return `${s.slice(0, m)}-${s.slice(m)}`;
  })();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`EasyJoin Code: ${formattedCode} Password: ${(code.joinPassword || '').replace(/-/g, '')}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // clipboard write failed silently
    }
  };

  return (
    <Card isExpired={isExpired} isNew={isNew}>
      <CardHeader>
        <CheckboxArea>
          <CheckboxButton
            type='button'
            isChecked={isSelected}
            isExpired={isExpired}
            onClick={(e) => { e.stopPropagation(); onToggleStar(code.id); }}
            title={isSelected ? 'Deselect' : 'Select'}
          >
            {isSelected && (
              <svg width='10' height='8' viewBox='0 0 10 8' fill='none'>
                <path d='M1 4L3.5 6.5L9 1' stroke='#77A7FF' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
              </svg>
            )}
          </CheckboxButton>
          <CardDescription>
            {code.description || <span style={{ color: '#CCCCCC', fontWeight: 400 }}>Untitled</span>}
            {isNew && (
              <span style={{
                marginLeft: '8px', color: '#D2A739', fontSize: '19px', fontWeight: 700,
              }}
              >
                New
              </span>
            )}
          </CardDescription>
        </CheckboxArea>
        <MenuWrapper ref={menuRef}>
          <MenuButton
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              const next = !isMenuOpen;
              setIsMenuOpen(next);
              onMenuOpenChange(next);
            }}
          >
            <Icon icon='dots' size={16} color='#9E9E9E' />
          </MenuButton>
          {isMenuOpen && (
            <MenuDropdown>
              <DropdownRow onClick={() => { closeMenu(); onEdit(code); }}>
                <Icon icon='edit-workout' size={16} color='#9E9E9E' styledProps={{ marginBottom: '2px' }} />
                Edit
              </DropdownRow>
              <DropdownRow
                isDanger
                onClick={() => { closeMenu(); onRequestDelete(code); }}
              >
                <Icon icon='delete' size={16} color='#FF0505' styledProps={{ marginBottom: '2px' }} />
                Delete
              </DropdownRow>
            </MenuDropdown>
          )}
        </MenuWrapper>
      </CardHeader>

      <CodeSection isExpired={isExpired}>
        <CodeFields>
          <CodeField>
            <Text
              styledWrapperProps={{ marginBottom: '2px' }}
              styledTextProps={{
                fontSize: '12px', fontWeight: 700, color: '#9E9E9E', letterSpacing: '1px',
              }}
              upperCase
            >
              Code
            </Text>
            <Text styledTextProps={{
              fontSize: '15px', fontWeight: 700, color: '#333333', letterSpacing: '2px',
            }}
            >
              {formattedCode}
            </Text>
          </CodeField>
          <CodeField>
            <Text
              styledWrapperProps={{ marginBottom: '2px' }}
              styledTextProps={{
                fontSize: '12px', fontWeight: 700, color: '#9E9E9E', letterSpacing: '1px',
              }}
              upperCase
            >
              Password
            </Text>
            <Text styledTextProps={{
              fontSize: '15px', fontWeight: 700, color: '#333333', letterSpacing: '2px',
            }}
            >
              {code.joinPassword ? code.joinPassword.replace(/-/g, '') : '—'}
            </Text>
          </CodeField>
        </CodeFields>
        <Button
          name={copied ? 'Copied!' : 'Copy'}
          icon={copied ? '' : 'duplicate'}
          iconColor={accentColor}
          iconSize={18}
          iconAfterText
          noBorder
          bgColor='transparent'
          txtColor={accentColor}
          customBorderRadius='6px'
          styledTextProps={{ fontSize: '16px', fontWeight: 700 }}
          styledWrapperProps={{ padding: '5px 10px', flexShrink: 0, flexBasis: '95px' }}
          onClick={handleCopy}
        />
      </CodeSection>

      <CardFooter>
        <FooterRow>
          <Text
            styledWrapperProps={{ flexShrink: 0, width: '80px' }}
            styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#9E9E9E' }}
            upperCase
          >
            Groups:
          </Text>
          <Text upperCase styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#9E9E9E' }}>
            {allGroupNames}
          </Text>
        </FooterRow>
        <FooterRow>
          <Text
            styledWrapperProps={{ flexShrink: 0, width: '80px' }}
            styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#9E9E9E' }}
            upperCase
          >
            Calendar:
          </Text>
          <Text upperCase styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#9E9E9E' }}>
            {calendarName}
          </Text>
        </FooterRow>
        <FooterRow>
          <Text
            styledWrapperProps={{ flexShrink: 0, width: '80px' }}
            styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#9E9E9E' }}
            upperCase
          >
            Valid Until:
          </Text>
          <FooterValue>
            {expiryDisplay}
            {code.expirationDate && (
              <Tooltip
                id={`expiry-info-${code.id}`}
                icon='info2'
                iconSize={13}
                hoverText={isExpired
                  ? `Expired at: ${code.expiredDisplayTime || code.expirationDate}`
                  : `Expires at: ${code.expiredDisplayTime || code.expirationDate}`}
                displayColor='#9E9E9E'
                tooltipStyles={{ fontSize: '13px' }}
                place='right'
                styledPropsWrapper={{
                  display: 'inline-flex',
                  flexShrink: 0,
                  overflow: 'visible',
                  mb: '2px',
                }}
              />
            )}
          </FooterValue>
        </FooterRow>
      </CardFooter>
    </Card>
  );
};

EasyJoinCard.propTypes = {
  code: PropTypes.shape({
    id: PropTypes.number,
    joinCode: PropTypes.string,
    joinPassword: PropTypes.string,
    description: PropTypes.string,
    expirationDate: PropTypes.string,
    expiredDisplayTime: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    calendar: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onRequestDelete: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onToggleStar: PropTypes.func.isRequired,
  onMenuOpenChange: PropTypes.func,
  isNew: PropTypes.bool,
};

EasyJoinCard.defaultProps = {
  onMenuOpenChange: () => {},
  isNew: false,
};

const EasyJoinCards = ({
  codes,
  isLoadingCodes,
  isDeleting,
  onEdit,
  onDelete,
  selectedIds,
  onToggleStar,
  newlyCreatedId,
}) => {
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [codeToDelete, setCodeToDelete] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (newlyCreatedId && containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [newlyCreatedId]);

  if (isLoadingCodes) {
    return <SpinnerContainer><Spinner /></SpinnerContainer>;
  }

  if (codes.length === 0) {
    return (
      <Text
        styledWrapperProps={{ textAlign: 'center', padding: '40px' }}
        styledTextProps={{ color: '#9E9E9E', fontSize: '14px' }}
      >
        You have no codes yet. Please create one above.
      </Text>
    );
  }

  return (
    <>
      <CardsContainer ref={containerRef}>
        {codes.map((code) => {
          const isMenuOpen = menuOpenId === code.id;
          const isNew = code.id === newlyCreatedId;
          return (
            <CardWrapper key={code.id} isMenuOpen={isMenuOpen}>
              <EasyJoinCard
                code={code}
                onEdit={onEdit}
                onRequestDelete={setCodeToDelete}
                isSelected={selectedIds.has(code.id)}
                onToggleStar={onToggleStar}
                onMenuOpenChange={(open) => setMenuOpenId(open ? code.id : null)}
                isNew={isNew}
              />
            </CardWrapper>
          );
        })}
      </CardsContainer>

      <Modal
        isOpen={!!codeToDelete}
        onRequestClose={() => setCodeToDelete(null)}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='400px'
        customMinHeight='200px'
      >
        <ModalContent>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            Delete Easy Join Code
          </Text>
          <Text styledWrapperProps={{ marginBottom: '24px' }}>
            {`Are you sure you want to delete the code ${(() => {
              const raw = codeToDelete?.joinCode;
              if (!raw) return '—';
              const s = raw.replace(/-/g, '');
              const m = Math.ceil(s.length / 2);
              return `${s.slice(0, m)}-${s.slice(m)}`;
            })()}? This action cannot be undone.`}
          </Text>
          <ModalFooter>
            <Button
              name='Cancel'
              fontSize='14px'
              bgColor='#FFF'
              borderColor='#EEEEEE'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={() => setCodeToDelete(null)}
            />
            <Button
              name={isDeleting ? 'Deleting...' : 'Delete'}
              fontSize='14px'
              bgColor='#FF0505'
              txtColor='#FFF'
              borderColor='#FF0505'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              disabled={isDeleting}
              onClick={() => {
                onDelete([codeToDelete.id]);
                setCodeToDelete(null);
              }}
            />
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

EasyJoinCards.propTypes = {
  codes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    joinCode: PropTypes.string,
    joinPassword: PropTypes.string,
    description: PropTypes.string,
    expirationDate: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    calendar: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  })).isRequired,
  isLoadingCodes: PropTypes.bool.isRequired,
  isDeleting: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  onToggleStar: PropTypes.func.isRequired,
  newlyCreatedId: PropTypes.number,
};

EasyJoinCards.defaultProps = {
  newlyCreatedId: null,
};

export default EasyJoinCards;
